import {cn} from '@tempmee/core'
import {OutlineXIcon} from '@tempmee/react-icons'
import type {VariantProps} from 'class-variance-authority'
import {
  type HTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
  forwardRef,
  useEffect,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import {Typography} from '../typography'
import {ToastContentVariants} from './Variants'

export type ToastSeverity = NonNullable<VariantProps<typeof ToastContentVariants>['severity']>

/** Why the toast asked to close (mirrors MUI's `Snackbar` close reasons + our close button). */
export type ToastCloseReason = 'timeout' | 'escapeKeyDown' | 'closeButton'

export interface ToastAnchorOrigin {
  vertical: 'top' | 'bottom'
  horizontal: 'left' | 'center' | 'right'
}

export interface ToastProps
  // `onClose` carries a reason arg, so it's omitted from the native handler set.
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onClose'> {
  /** Whether the toast is visible. */
  open: boolean
  /** Color scheme of the bar. @default 'default' */
  severity?: ToastSeverity
  /** The message text (or node). */
  message?: ReactNode
  /** Optional trailing action (e.g. a small text `Button`). MUI's `action` slot. */
  action?: ReactNode
  /**
   * Called when the toast requests to close — via the auto-hide timer
   * (`'timeout'`), Escape (`'escapeKeyDown'`), or the close button
   * (`'closeButton'`).
   */
  onClose?: (event: SyntheticEvent | KeyboardEvent | null, reason: ToastCloseReason) => void
  /**
   * Auto-dismiss after this many ms. Omit (or `null`) to keep it open until
   * dismissed — matching MUI's `Snackbar` default (no auto-hide).
   */
  autoHideDuration?: number | null
  /** Where the toast is pinned on screen. @default { vertical: 'bottom', horizontal: 'left' } */
  anchorOrigin?: ToastAnchorOrigin
  /** Show the trailing close (×) button. @default true */
  showCloseButton?: boolean
  /** Accessible label for the close button. @default 'Close' */
  closeLabel?: string
  /** Enter/exit transition duration in ms (MUI `Grow`). @default 225 */
  transitionDuration?: number
}

const ANCHOR_VERTICAL: Record<ToastAnchorOrigin['vertical'], string> = {
  top: 'top-0',
  bottom: 'bottom-0',
}

const ANCHOR_HORIZONTAL: Record<ToastAnchorOrigin['horizontal'], string> = {
  left: 'left-0 justify-start',
  center: 'inset-x-0 justify-center',
  right: 'right-0 justify-end',
}

/**
 * Brief, auto-dismissing notification (MUI's `Snackbar`). Portals to the
 * document body and pins itself to a screen corner/edge via `anchorOrigin`,
 * auto-hiding after `autoHideDuration`. The colored bar carries the message, an
 * optional `action`, and a close button.
 *
 * **Use a Toast when:**
 * - Confirming a transient outcome ("Shift posted", "Changes saved").
 * - The message is non-blocking and time-limited.
 *
 * **Do NOT use a Toast when:**
 * - The message must persist in context — use {@link Alert}.
 * - The content is promotional/brand-forward — use `Banner`.
 *
 * @see Alert — for inline, persistent contextual messages.
 */
export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      open,
      severity = 'default',
      message,
      action,
      onClose,
      autoHideDuration,
      anchorOrigin = {vertical: 'bottom', horizontal: 'left'},
      showCloseButton = true,
      closeLabel = 'Close',
      transitionDuration = 225,
      ...props
    },
    ref,
  ) => {
    // Keep mounted through the exit transition (MUI's Grow stays mounted while
    // animating out). `entered` drives the enter/exit visual state.
    const [mounted, setMounted] = useState(open)
    const [entered, setEntered] = useState(false)

    useEffect(() => {
      if (open) {
        setMounted(true)
        // Flip to the entered state on the next frame so the transition runs.
        const raf = requestAnimationFrame(() => setEntered(true))
        return () => cancelAnimationFrame(raf)
      }
      setEntered(false)
      const id = setTimeout(() => setMounted(false), transitionDuration)
      return () => clearTimeout(id)
    }, [open, transitionDuration])

    // Auto-hide timer
    useEffect(() => {
      if (!open || autoHideDuration == null) return
      const id = setTimeout(() => onClose?.(null, 'timeout'), autoHideDuration)
      return () => clearTimeout(id)
    }, [open, autoHideDuration, onClose])

    // Escape to close
    useEffect(() => {
      if (!open) return
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') onClose?.(event, 'escapeKeyDown')
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }, [open, onClose])

    if (!mounted) return null

    return createPortal(
      <div
        className={cn(
          'pointer-events-none fixed z-[1400] flex p-6',
          ANCHOR_VERTICAL[anchorOrigin.vertical],
          ANCHOR_HORIZONTAL[anchorOrigin.horizontal],
        )}
      >
        <div
          ref={ref}
          // biome-ignore lint/a11y/useSemanticElements: role="alert" announces the toast to AT; no native element provides it
          role="alert"
          style={{transitionDuration: `${transitionDuration}ms`}}
          className={cn(
            ToastContentVariants({severity}),
            // MUI Grow: scale 0.75 → 1 + fade, origin per vertical anchor.
            'transition-[opacity,transform] ease-out',
            anchorOrigin.vertical === 'top' ? 'origin-top' : 'origin-bottom',
            entered ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
            className,
          )}
          {...props}
        >
          <Typography variant="body1" className="min-w-0 break-words text-white">
            {message}
          </Typography>

          {(action != null || showCloseButton) && (
            <div className="flex shrink-0 items-center gap-2">
              {action}
              {showCloseButton && (
                <button
                  type="button"
                  aria-label={closeLabel}
                  onClick={(event) => onClose?.(event, 'closeButton')}
                  className="flex size-[38px] shrink-0 items-center justify-center rounded-2xl text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 [&_svg]:size-[22px]"
                >
                  <OutlineXIcon />
                </button>
              )}
            </div>
          )}
        </div>
      </div>,
      document.body,
    )
  },
)

Toast.displayName = 'Toast'
